<template>
    <Layout>
        <PageHeader :title="title" :items="items" />

        <div v-if="canCreatePsychologyPatient" class="row justify-content-center">
            <div class="col-12 col-xl-12">
                <div class="card border-0 shadow-sm">
                    <div class="card-body">
                        <form @submit.prevent="AddPatient">
                            <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                                <button @click="$router.push('/psychology')" class="btn btn-sm btn-secondary"><i
                                        class="mdi mdi-undo"></i> Cancel</button>
                                <h5 class="mb-0">Add Psychology Patient</h5>
                                <div class="d-flex align-items-center gap-2">
                                    <span class="badge p-2"
                                        :class="currentStep === 1 ? 'bg-primary' : 'bg-soft-primary text-primary'">1.
                                        Identification</span>
                                    <span class="badge p-2"
                                        :class="currentStep === 2 ? 'bg-primary' : 'bg-soft-primary text-primary'">2.
                                        Presentation <small>(Optional)</small>
                                    </span>
                                </div>
                            </div>

                            <div class="progress progress-sm mb-4">
                                <div class="progress-bar" role="progressbar"
                                    :style="{ width: `${(currentStep / totalSteps) * 100}%` }"
                                    :aria-valuenow="currentStep" aria-valuemin="1" :aria-valuemax="totalSteps"></div>
                            </div>

                            <div class="row g-3" v-if="currentStep === 1">
                                <div class="col-md-6">
                                    <div class="card border bg-light h-100">
                                        <div class="card-body">
                                            <h6 class="mb-3 text-primary">Basic Information</h6>

                                            <div class="form-group mb-3">
                                                <label class="mb-0 form-label">Date</label>
                                                <input v-model="registrationDate" type="date" class="form-control" />
                                            </div>

                                            <div class="form-group mb-3">
                                                <label class="mb-0 form-label">Psychology Category <span class="text-danger">*</span></label>
                                                <multiselect
                                                    v-model="selectedCategory"
                                                    :options="allowedPsychologyCategories"
                                                    label="name"
                                                    track-by="id"
                                                    :show-labels="false"
                                                    placeholder="Select psychology category"
                                                    @input="onCategoryChange"
                                                />
                                            </div>

                                            <div class="form-group mb-3">
                                                <label class="mb-0 form-label">Psychology Service <span class="text-danger">*</span></label>
                                                <multiselect
                                                    v-model="selectedService"
                                                    :options="serviceOptions"
                                                    label="name"
                                                    track-by="id"
                                                    :show-labels="false"
                                                    placeholder="Select psychology service"
                                                />
                                            </div>

                                            <div class="form-group mb-3">
                                                <label class="mb-0 form-label">First & Last Name <span
                                                        class="text-danger">*</span></label>
                                                <input v-model.trim="fullName" type="text" class="form-control"
                                                    placeholder="Enter full name" />
                                            </div>

                                            <div class="row">
                                                <div class="col-6">
                                                    <div class="form-group mb-3">
                                                        <label class="mb-0 form-label">Gender <span
                                                                class="text-danger">*</span></label>
                                                        <select v-model.number="gender" class="form-select">
                                                            <option :value="null" disabled>Select gender</option>
                                                            <option :value="1">Male</option>
                                                            <option :value="2">Female</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div class="col-6">
                                                    <div class="form-group mb-3" v-if="useAgeOrDob == 'age'">
                                                        <label class="mb-0 form-label"><input type="radio"
                                                                v-model="useAgeOrDob" value="age" /> Age <span
                                                                class="text-danger">*</span> <input type="radio"
                                                                v-model="useAgeOrDob" value="dob" class="ms-5" /> Date
                                                            of Birth <span class="text-danger">*</span></label>
                                                        <input v-model.number="age" type="number" min="0"
                                                            class="form-control" placeholder="Enter age" />
                                                    </div>
                                                    <div class="form-group mb-3" v-if="useAgeOrDob == 'dob'">
                                                        <label class="mb-0 form-label"><input type="radio"
                                                                v-model="useAgeOrDob" value="age" /> Age <span
                                                                class="text-danger">*</span> <input type="radio"
                                                                v-model="useAgeOrDob" value="dob" class="ms-5" /> Date
                                                            of Birth <span class="text-danger">*</span></label>
                                                        <input v-model="dateOfBirth" type="date" class="form-control" />
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="form-group mb-3">
                                                <label class="mb-0 form-label">Marital Status</label>
                                                <select v-model="maritalStatus" class="form-select">
                                                    <option value="">Select status</option>
                                                    <option>Single</option>
                                                    <option>Married</option>
                                                    <option>Separated</option>
                                                    <option>Divorced</option>
                                                    <option>Widowed</option>
                                                </select>
                                            </div>

                                            <div class="form-group mb-3">
                                                <label class="mb-0 form-label">Address</label>
                                                <input v-model.trim="address" type="text" class="form-control"
                                                    placeholder="Enter address" />
                                            </div>

                                            <div class="form-group mt-3">
                                                <label class="mb-0 form-label">Next of Kin</label>
                                                <input v-model.trim="nok" type="text" class="form-control"
                                                    placeholder="Next of kin name" />
                                            </div>
                                            <div class="form-group mb-0 mt-3">
                                                <label class="mb-0 form-label">Next of Kin Contact</label>
                                                <input v-model.trim="nok_contact" type="text" class="form-control"
                                                    placeholder="Next of kin contact" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-md-6">
                                    <div class="card border bg-light h-100">
                                        <div class="card-body">
                                            <h6 class="mb-3 text-primary">Background</h6>

                                            <div class="form-group mb-3">
                                                <label class="mb-0 form-label">Nationality</label>
                                                <multiselect v-model="nationality" :options="countries" label="name"
                                                    track-by="name" :show-labels="false"
                                                    placeholder="Select nationality" />
                                            </div>

                                            <div class="form-group mb-3">
                                                <label class="mb-0 form-label">Residential Status</label>
                                                <select v-model.number="residential_status" class="form-select">
                                                    <option :value="null" disabled>Select option</option>
                                                    <option :value="1">Resident</option>
                                                    <option :value="2">Internally Displaced</option>
                                                    <option :value="3">Refugee</option>
                                                    <option :value="4">Migrant</option>
                                                </select>
                                            </div>

                                            <div class="row">
                                                <div class="col-6">
                                                    <div class="form-group mb-3">
                                                        <label class="mb-0 form-label">Level of Education</label>
                                                        <input v-model.trim="education" type="text" class="form-control"
                                                            placeholder="Enter level" />
                                                    </div>
                                                </div>
                                                <div class="col-6">
                                                    <div class="form-group mb-3">
                                                        <label class="mb-0 form-label">Occupation</label>
                                                        <input v-model.trim="occupation" type="text"
                                                            class="form-control" placeholder="Enter occupation" />
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="form-group mb-3">
                                                <label class="mb-0 form-label">Siblings</label>
                                                <input v-model="siblings" type="text" class="form-control">
                                            </div>

                                            <div class="form-group mb-0">
                                                <label class="mb-0 form-label">Referred By</label>
                                                <input v-model.trim="referredBy" type="text" class="form-control"
                                                    placeholder="Referral source" />
                                            </div>

                                            <div v-if="showExtraInfoField" class="form-group mb-0 mt-3">
                                                <label class="mb-0 form-label">{{ extraInfoLabel }}</label>
                                                <textarea v-model.trim="psychology_extra_info" class="form-control" rows="3"
                                                    placeholder="Provide any extra relevant information"></textarea>
                                            </div>

                                            <div v-if="selectedPsychologyCategoryKey === 'psychology_assessments_tests'" class="form-group mb-0 mt-3">
                                                <label class="mb-0 form-label">Test Type</label>
                                                <input v-model.trim="psychology_test_type" type="text" class="form-control"
                                                    placeholder="Enter the test type" />
                                            </div>

                                            <div v-if="selectedPsychologyCategoryKey === 'psychology_corporate_services'" class="row mt-1">
                                                <div class="col-7">
                                                    <div class="form-group mb-0 mt-3">
                                                        <label class="mb-0 form-label">Subscribed Company</label>
                                                        <input v-model.trim="psychology_company_name" type="text" class="form-control"
                                                            placeholder="Company or institution name" />
                                                    </div>
                                                </div>
                                                <div class="col-5">
                                                    <div class="form-group mb-0 mt-3">
                                                        <label class="mb-0 form-label">Employees Covered</label>
                                                        <input v-model="psychology_employee_coverage" type="number" min="0" class="form-control"
                                                            placeholder="0" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="row g-3" v-if="currentStep === 2">
                                <div class="col-12 mb-0">
                                    <div class="alert alert-info d-flex align-items-center mb-0" role="alert">
                                        <i class="mdi mdi-information-outline me-2 fs-5"></i>
                                        <span>This section is <strong>optional</strong>. You can fill in the
                                            presentation details now or skip and submit directly.</span>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="card border bg-light h-100">
                                        <div class="card-body">
                                            <h6 class="mb-3 text-primary">Mental State Examination</h6>

                                            <div class="form-group mb-3">
                                                <label class="mb-0 form-label">Mood</label>
                                                <tinymce id="mood-editor" v-model="mood"></tinymce>
                                            </div>

                                            <div class="form-group mb-3">
                                                <label class="mb-0 form-label">Appearance and Hygiene</label>
                                                <tinymce id="appearance-editor" v-model="appearanceHygiene"></tinymce>
                                            </div>

                                        </div>
                                    </div>
                                </div>

                                <div class="col-md-6">
                                    <div class="card border bg-light h-100">
                                        <div class="card-body">
                                            <h6 class="mb-3 text-primary">Cognitive & Affective Tone</h6>

                                            <div class="form-group mb-3">
                                                <label class="mb-0 form-label">Eye Contact</label>
                                                <select v-model.number="eyeContact" class="form-select">
                                                    <option :value="null" disabled>Select eye contact</option>
                                                    <option :value="1">None</option>
                                                    <option :value="2">Normal</option>
                                                    <option :value="3">Avoidant</option>
                                                    <option :value="4">Poor</option>
                                                </select>
                                            </div>

                                            <div class="form-group mb-3">
                                                <label class="mb-0 form-label">Memory</label>
                                                <select v-model.number="memoryStatus" class="form-select">
                                                    <option :value="null" disabled>Select memory status</option>
                                                    <option :value="1">Normal</option>
                                                    <option :value="2">Impaired</option>
                                                </select>
                                            </div>

                                            <div class="form-group mb-3">
                                                <label class="mb-0 form-label">Affective Tone</label>
                                                <select v-model.number="affectiveTone" class="form-select">
                                                    <option :value="null" disabled>Select affective tone</option>
                                                    <option :value="1">Normal</option>
                                                    <option :value="2">Labile</option>
                                                    <option :value="3">Blunted</option>
                                                    <option :value="4">Indifferent</option>
                                                </select>
                                            </div>

                                            <div class="form-group mb-0">
                                                <label class="mb-0 form-label">Interaction Style</label>
                                                <select v-model.number="interactionStyle" class="form-select">
                                                    <option :value="null" disabled>Select interaction</option>
                                                    <option :value="1">Dramatic</option>
                                                    <option :value="2">Sensitive</option>
                                                    <option :value="3">Cooperative</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="mt-4">
                                <div v-if="error" class="alert alert-danger mb-3">{{ error }}</div>
                                <div v-if="successMessage" class="alert alert-success mb-3">{{ successMessage }}</div>

                                <div class="d-flex flex-wrap gap-2">
                                    <b-button type="button" variant="outline-secondary"
                                        :disabled="currentStep === 1 || isSubmitting" @click="prevStep">
                                        Back
                                    </b-button>
                                    <b-button v-if="currentStep < totalSteps" type="button" variant="outline-primary"
                                        class="ms-auto" :disabled="isSubmitting" @click="AddPatient">
                                        {{ isSubmitting ? 'Saving...' : 'Skip & Submit' }}
                                    </b-button>
                                    <b-button v-if="currentStep < totalSteps" type="button" variant="primary"
                                        @click="nextStep">
                                        Next
                                    </b-button>
                                    <b-button v-else type="button" variant="primary" class="ms-auto"
                                        :disabled="isSubmitting" @click="AddPatient">
                                        {{ isSubmitting ? 'Saving...' : 'Create Patient' }}
                                    </b-button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>

        <div v-else>
            <div class="row justify-content-center">
                <div class="col-md-8 col-xl-6">
                    <div class="card border-0 shadow-sm">
                        <div class="card-body text-center p-5">
                            <div class="mb-4">
                                <div class="avatar-xl mx-auto">
                                    <div class="avatar-title bg-primary bg-opacity-10 rounded-circle">
                                        <i class="mdi mdi-account-plus text-primary" style="font-size: 48px;"></i>
                                    </div>
                                </div>
                            </div>
                            <h5 class="text-primary mb-3">Access Restricted</h5>
                            <p class="text-muted mb-4">You don't have permission to add psychology patients. Please contact
                                your system administrator.</p>
                            <img src="@/assets/images/profile-img.png" alt class="img-fluid"
                                style="max-width: 300px; opacity: 0.6;" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>

<script>
import axios from "axios";
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import Multiselect from "vue-multiselect";
import { countriesMethods, countriesComputed, serviceCategoryMethods, serviceCategoryComputed } from "@/state/helpers";

export default {
    page: {
        title: "Add Psychology Patient",
        meta: [{ name: "description", content: appConfig.description }],
    },
    components: {
        Layout,
        PageHeader,
        Multiselect,
    },
    data() {
        return {
            userData: JSON.parse(localStorage.getItem("user")),
            title: "Add Psychology Patient",
            items: [{ text: "Dashboard", to: "/" }, { text: "Psychology", to: "/psychology" }, { text: "New Patient", active: true }],
            currentStep: 1,
            totalSteps: 2,
            isSubmitting: false,
            successMessage: "",
            error: "",
            registrationDate: new Date().toLocaleDateString("fr-CA"),
            selectedCategory: null,
            selectedService: null,
            serviceOptions: [],
            psychology_extra_info: "",
            psychology_company_name: "",
            psychology_employee_coverage: "",
            psychology_test_type: "",
            fullName: "",
            gender: null,
            age: null,
            maritalStatus: "",
            address: "",
            nationality: { name: "Uganda" },
            residential_status: null,
            education: "",
            occupation: "",
            siblings: '',
            siblingInput: "",
            referredBy: "",
            nok: "",
            nok_contact: "",
            mood: "",
            appearanceHygiene: "",
            interactionStyle: null,
            eyeContact: null,
            memoryStatus: null,
            affectiveTone: null,
            useAgeOrDob: "age", // New field to toggle between age and date of birth
            dateOfBirth: null, // New field for date of birth
        };
    },
    computed: {
        ...countriesComputed,
        ...serviceCategoryComputed,
        isAdministrator() {
            return (this.userData?.role || "").toString().toLowerCase() === "administrator";
        },
        canViewPsychology() {
            return this.isAdministrator || this.userData?.permissions?.['view_psychology'] == 1;
        },
        canCreatePsychologyPatient() {
            return this.isAdministrator || this.userData?.permissions?.['create_psychology_patient'] == 1;
        },
        allowedPsychologyCategories() {
            return this.servicecategories.filter((category) => category.system_key && category.system_key.startsWith("psychology_"));
        },
        selectedPsychologyCategoryKey() {
            return this.selectedCategory?.system_key || "";
        },
        showExtraInfoField() {
            return !!this.selectedCategory;
        },
        extraInfoLabel() {
            if (this.selectedPsychologyCategoryKey === "psychology_group_programs") {
                return "Participants / Session Notes";
            }
            if (this.selectedPsychologyCategoryKey === "psychology_sensitive_services") {
                return "Additional Sensitive Service Context";
            }
            if (this.selectedPsychologyCategoryKey === "psychology_corporate_services") {
                return "Additional Corporate Service Information";
            }
            if (this.selectedPsychologyCategoryKey === "psychology_assessments_tests") {
                return "Additional Assessment Information";
            }
            return "Additional Psychology Information";
        },
    },
    created() {
        this.fetchCountries();
        this.fetchServiceCategory();
    },
    methods: {
        ...countriesMethods,
        ...serviceCategoryMethods,
        psychologyRouteForCategory(categoryKey) {
            const routeMap = {
                psychology_consultations: "/psychology/consultations",
                psychology_group_programs: "/psychology/group-therapies",
                psychology_assessments_tests: "/psychology/assessments",
                psychology_corporate_services: "/psychology/corporate",
                psychology_sensitive_services: "/psychology/sensitive",
            };

            return routeMap[categoryKey] || "/psychology";
        },
        async applyPrefilledCategory() {
            const categoryKey = this.$route.query.category_key;
            if (!categoryKey || !this.allowedPsychologyCategories.length) {
                return;
            }

            const matchedCategory = this.allowedPsychologyCategories.find((category) => category.system_key === categoryKey);
            if (!matchedCategory) {
                return;
            }

            this.selectedCategory = matchedCategory;
            await this.onCategoryChange();
        },
        nextStep() {
            this.error = "";
            if (!this.validateStep(1)) return;
            this.currentStep = 2;
        },
        prevStep() {
            this.error = "";
            this.currentStep = 1;
        },
        validateStep(step) {
            if (step === 1) {
                if (!this.fullName) {
                    this.error = "Patient full name is required.";
                    return false;
                }
                if (!this.gender) {
                    this.error = "Please select the patient's gender.";
                    return false;
                }
                if (!this.selectedCategory) {
                    this.error = "Please select the psychology category.";
                    return false;
                }
                if (!this.selectedService) {
                    this.error = "Please select the psychology service.";
                    return false;
                }
                if (this.useAgeOrDob === "age" && (this.age === null || this.age === "" || Number(this.age) < 0)) {
                    this.error = "Please provide a valid age.";
                    return false;
                }
                if (this.useAgeOrDob === "dob" && !this.dateOfBirth) {
                    this.error = "Please provide the date of birth.";
                    return false;
                }
            }

            return true;
        },
        resetForm() {
            this.registrationDate = new Date().toLocaleDateString("fr-CA");
            this.selectedCategory = null;
            this.selectedService = null;
            this.serviceOptions = [];
            this.psychology_extra_info = "";
            this.psychology_company_name = "";
            this.psychology_employee_coverage = "";
            this.psychology_test_type = "";
            this.fullName = "";
            this.gender = null;
            this.age = null;
            this.maritalStatus = "";
            this.address = "";
            this.residential_status = null;
            this.education = "";
            this.occupation = "";
            this.siblings = '';
            this.siblingInput = "";
            this.referredBy = "";
            this.nok = "";
            this.nok_contact = "";
            this.mood = "";
            this.appearanceHygiene = "";
            this.interactionStyle = null;
            this.eyeContact = null;
            this.memoryStatus = null;
            this.affectiveTone = null;
            this.currentStep = 1;
            this.error = "";
        },
        async onCategoryChange() {
            this.selectedService = null;
            this.serviceOptions = [];

            if (!this.selectedCategory?.id) {
                return;
            }

            const response = await axios.get(`/servicebycategory/${this.selectedCategory.id}`);
            this.serviceOptions = response.data.services || [];
        },
        async AddPatient() {
            this.error = "";
            this.successMessage = "";

            if (!this.validateStep(1)) return;

            this.isSubmitting = true;
            try {
                const payload = new FormData();
                payload.append("date_of_registration", this.registrationDate);
                payload.append("psychology_category_key", this.selectedCategory?.system_key || "");
                payload.append("psychology_category_name", this.selectedCategory?.name || "");
                payload.append("psychology_service_id", this.selectedService?.id || "");
                payload.append("psychology_service_name", this.selectedService?.name || "");
                payload.append("psychology_extra_info", this.psychology_extra_info || "");
                payload.append("company_name", this.psychology_company_name || "");
                payload.append("employee_coverage", this.psychology_employee_coverage || "");
                payload.append("test_type", this.psychology_test_type || "");
                payload.append("full_name", this.fullName);
                payload.append("gender", this.gender);
                if (this.useAgeOrDob === "age") {
                    let dob = new Date();
                    dob.setFullYear(dob.getFullYear() - this.age);
                    payload.append("date_of_birth", dob.toISOString().split("T")[0]);
                } else {
                    payload.append("date_of_birth", this.dateOfBirth ?? "");
                }
                payload.append("age", this.age ?? "");
                payload.append("marital_status", this.maritalStatus);
                payload.append("address", this.address);
                payload.append("nationality", this.nationality && this.nationality.name ? this.nationality.name : "");
                payload.append("residential_status", this.residential_status ?? "");
                payload.append("education", this.education);
                payload.append("occupation", this.occupation);
                payload.append("siblings", this.siblings);
                payload.append("referred_by", this.referredBy);
                payload.append("nok", this.nok ?? "");
                payload.append("nok_contact", this.nok_contact ?? "");

                // Only append presentation fields if they have values
                if (this.mood) {
                    payload.append("mood", this.mood);
                }
                if (this.appearanceHygiene) {
                    payload.append("appearance", this.appearanceHygiene);
                }
                if (this.interactionStyle !== null) {
                    payload.append("interaction_style", this.interactionStyle);
                }
                if (this.eyeContact !== null) {
                    payload.append("eye_contact", this.eyeContact);
                }
                if (this.memoryStatus !== null) {
                    payload.append("memory", this.memoryStatus);
                }
                if (this.affectiveTone !== null) {
                    payload.append("affection_tone", this.affectiveTone);
                }

                const response = await axios.post("/new-psycho-patient", payload);
                if (response.status != 200) {
                    throw new Error(response.data.message || "Failed to create psychology patient.");
                }
                this.successMessage = "Psychology patient created successfully.";
                const destination = this.psychologyRouteForCategory(this.selectedPsychologyCategoryKey);
                this.resetForm();
                this.$router.push(destination);
            } catch (e) {
                const apiMessage = e?.response?.data?.message;
                this.error = apiMessage || "Failed to create psychology patient.";
            } finally {
                this.isSubmitting = false;
            }
        },
    },
    watch: {
        servicecategories: {
            immediate: true,
            handler() {
                this.applyPrefilledCategory();
            },
        },
    },
};
</script>

<style scoped>
.badge {
    font-size: 0.75rem;
}

.bg-soft-primary {
    background: rgba(85, 110, 230, 0.18);
}

.progress-sm {
    height: 6px;
}

.avatar-xl {
    width: 80px;
    height: 80px;
}

.avatar-title {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}

.chip-text {
    font-weight: 500;
}

.chip-remove {
    background: none;
    border: none;
    color: #556ee6;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    line-height: 1;
    transition: color 0.2s ease;
}

.chip-remove:hover {
    color: #3f4ec7;
}

.siblings-input {
    flex: 1 1 150px;
    border: none !important;
    box-shadow: none !important;
    padding: 0.375rem 0.5rem !important;
    min-height: auto;
}

.siblings-input:focus {
    border: none !important;
    box-shadow: none !important;
}

@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateX(-10px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@media (max-width: 768px) {
    .card-body {
        padding: 1rem;
    }
}
</style>
